import { useEffect, useRef, useState } from 'react';
import { testBotMessage } from '../../api/services/botService';

/**
 * Configuration preview mode: talk to the bot with its saved configuration (tone, language, boundaries, handoff)
 * without leaving the page. Uses POST /api/bots/{id}/test-message, which counts as one AI reply.
 */
export function PreviewChat({ botId, bot, primary }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  useEffect(() => { setMessages([]); setError(''); setDraft(''); }, [botId]);
  useEffect(() => { if (endRef.current && endRef.current.scrollIntoView) endRef.current.scrollIntoView({ block: 'nearest' }); }, [messages, busy]);

  async function send(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) { setError('Type a message to try the bot.'); return; }
    if (text.length > 1000) { setError('Messages can be at most 1000 characters.'); return; }
    setError(''); setDraft('');
    setMessages((m) => [...m, { from: 'visitor', text }]);
    setBusy(true);
    try {
      const res = await testBotMessage(botId, text);
      setMessages((m) => [...m, { from: 'bot', text: res.reply, note: res.escalate ? 'This would hand the visitor to a human agent.' : (res.matched === false ? 'Fallback message used.' : '') }]);
    } catch (err) {
      setError(err.code === 'AI_QUOTA_EXCEEDED' ? 'Your workspace has used all its AI replies this month, so the preview cannot answer.' : (err.message || 'The preview could not answer. Try again.'));
    } finally { setBusy(false); }
  }

  return (
    <div className="bc-tryit">
      <div className="bc-tryit-note">Answers use this bot’s saved settings: <strong>{bot.tone}</strong> tone, <strong>{bot.primaryLanguage}</strong>. Each reply counts toward your monthly AI replies.</div>
      <div className="bc-tryit-log" role="log" aria-live="polite" aria-label="Preview conversation">
        <div className="bc-bubble">{bot.greeting || 'Hi! How can I help you today?'}</div>
        {messages.map((m, i) => (
          <div key={i} className={m.from === 'visitor' ? 'bc-user-bubble' : 'bc-bubble'} style={m.from === 'visitor' ? { background: primary } : undefined}>
            {m.text}
            {m.note && <small className="bc-tryit-flag">{m.note}</small>}
          </div>
        ))}
        {busy && <div className="bc-bubble bc-typing" role="status">Thinking…</div>}
        <div ref={endRef} />
      </div>
      {error && <div className="error-text" role="alert">{error}</div>}
      <form className="bc-tryit-form" onSubmit={send}>
        <label className="visually-hidden" htmlFor="bc-tryit-input">Message to try</label>
        <input id="bc-tryit-input" value={draft} maxLength={1000} onChange={(e) => setDraft(e.target.value)} placeholder="Ask something your visitors would ask…" disabled={busy} />
        <button type="submit" className="btn-primary" disabled={busy}>Send</button>
        {messages.length > 0 && <button type="button" className="btn-secondary" onClick={() => { setMessages([]); setError(''); }} disabled={busy}>Clear</button>}
      </form>
    </div>
  );
}
